import { useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchEmployeeInput } from "./components/SearchEmployeeInput";
import { EmployeeShiftInfo } from "./components/EmployeeShiftInfo";
import { JustificationInput } from "./form/JustificationInput";
import { getMinOvertimeDate } from "@/lib/utils";
import { useOvertimeRequestForm } from "./hooks/useOvertimeRequestForm";
import { UserRole } from "@/types";
import { useAuth } from "@/contexts/auth";
import { fetchActiveShiftsForCompany } from "@/services/employeeShiftService";
import { useState, useMemo } from "react";
import { hasShiftWorkingHoursForDate, getShiftDayNumber } from "@/utils/shiftFormUtils";
import { isOvertimeOverlapWithShift, isOvertimeCompletelyWithinShift } from "@/utils/dateTimeHelpers";
import { ShiftSelector } from "@/components/common/ShiftSelector";
import ShiftInfoTable from "./components/ShiftInfoTable";
import { Shift } from "@/types/shifts";
import { useDutyRosterEmployeeData } from "@/hooks/useDutyRosterEmployeeData";
import { normalizeResidency, RESIDENCY_VALUES } from "@/utils/residencyUtils";

interface NewOvertimeRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userRole?: UserRole;
  userDepartment?: string[];
  companyId?: string | null;
}

export function NewOvertimeRequestModal({
  open,
  onOpenChange,
  userRole,
  userDepartment,
  companyId
}: NewOvertimeRequestModalProps) {
  const { user } = useAuth();
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [selectedShiftId, setSelectedShiftId] = useState<string>('');
  const [showShiftSelection, setShowShiftSelection] = useState(false);
  const [selectedShiftData, setSelectedShiftData] = useState<Shift | null>(null);
  const [shiftDataLoaded, setShiftDataLoaded] = useState(false);
  const [hasWorkingHours, setHasWorkingHours] = useState(true);
  const [timeValidationError, setTimeValidationError] = useState<string | null>(null);

  const {
    selectedEmployee,
    setSelectedEmployee,
    fromDate,
    setFromDate,
    startTime, 
    setStartTime,
    endTime,
    setEndTime,
    justification,
    setJustification,
    error,
    submitting,
    totalHours,
    totalHoursError,
    totalHoursIsValid,
    shiftOverlapError,
    hasShiftOverlap,
    overtimeStats,
    // Holiday information
    holidayInfo,
    isHoliday,
    willDeductBreak,
    breakDeductionNotice,
    // Functions
    resetForm,
    handleSubmit
  } = useOvertimeRequestForm({
    onSuccess: () => {
      // Modal already closed instantly on click, just reset form
      resetForm();
      setSelectedShiftId('');
      setShowShiftSelection(false);
      setSelectedShiftData(null);
      setShiftDataLoaded(false);
      setHasWorkingHours(true);
      setTimeValidationError(null);
    },
    companyId,
    selectedShiftData
  });

  // Get duty roster employee data for shift filtering (after selectedEmployee is defined)
  const { data: dutyRosterData } = useDutyRosterEmployeeData(selectedEmployee?.id || null, fromDate);

  // Calculate monthly hours for selected employee
  const monthlyHours = useMemo(() => {
    if (!selectedEmployee?.id || !overtimeStats) return 0;
    return overtimeStats[selectedEmployee.id] || 0;
  }, [selectedEmployee?.id, overtimeStats]);

  // Filter shifts based on duty roster employee constraints
  const filteredShifts = useMemo(() => {
    if (!dutyRosterData?.isDutyRoster || !dutyRosterData.dateValidation) {
      // For non-duty roster employees, check residency type
      const isResident = selectedEmployee && normalizeResidency(selectedEmployee.residency) === RESIDENCY_VALUES.RESIDENCE;
      
      // Residence employees: show only shifts with in_working_hours = FALSE
      // Non-Residence employees: show all shifts
      return isResident ? shifts.filter(shift => shift.in_working_hours === false) : shifts;
    }

    const { dateValidation } = dutyRosterData;

    if (dateValidation.isOffDay || dateValidation.isHolidayOverride) {
      // For off days and holiday overrides, show shifts with in_working_hours = TRUE
      return shifts.filter(shift => shift.in_working_hours === true);
    }

    if (dateValidation.isWorkingDay && dateValidation.availableShifts) {
      // For working days, only show available shifts (in_working_hours = TRUE, excluding current shift)
      return shifts.filter(shift => 
        shift.in_working_hours === true && 
        shift.id !== dateValidation.currentShift?.id
      );
    }

    // Fallback: non-duty roster employees - check residency type
    const isResident = selectedEmployee && normalizeResidency(selectedEmployee.residency) === RESIDENCY_VALUES.RESIDENCE;
    return isResident ? shifts.filter(shift => shift.in_working_hours === false) : shifts;
  }, [shifts, dutyRosterData, selectedEmployee]);

  // Calculate if submit button should be disabled - comprehensive validation
  const isSubmitDisabled = useMemo(() => {
    return (
      submitting ||
      !totalHoursIsValid ||
      hasShiftOverlap ||
      !!timeValidationError ||
      !selectedEmployee ||
      !fromDate ||
      !startTime ||
      !endTime ||
      !justification.trim() ||
      (showShiftSelection && !selectedShiftData)
    );
  }, [
    submitting,
    totalHoursIsValid,
    hasShiftOverlap,
    timeValidationError,
    selectedEmployee,
    fromDate,
    startTime,
    endTime,
    justification,
    showShiftSelection,
    selectedShiftData
  ]);

  // Reset form values when modal is opened/closed
  useEffect(() => {
    if (open) {
      // Reset all form states immediately when modal opens
      resetForm(); // Reset form hook state
      
      // Reset modal-specific states
      setSelectedShiftId('');
      setShowShiftSelection(false);
      setSelectedShiftData(null);
      setShiftDataLoaded(false);
      setHasWorkingHours(true);
      setTimeValidationError(null);
    } else {
      // Also reset when modal closes to ensure clean state for next open
      resetForm();
      setSelectedShiftId('');
      setShowShiftSelection(false);
      setSelectedShiftData(null);
      setShiftDataLoaded(false);
      setHasWorkingHours(true);
      setTimeValidationError(null);
    }
  }, [open, resetForm]);

  // Fetch shifts when component mounts (background loading) - not when modal opens
  useEffect(() => {
    const fetchShifts = async () => {
      if (!user?.companyId || shifts.length > 0) return; // Don't refetch if already loaded
      try {
        const shiftsData = await fetchActiveShiftsForCompany(user.companyId);
        // Data is already properly typed from the service
        setShifts(shiftsData);
      } catch {
        // Handle error silently
      }
    };
    fetchShifts();
  }, [user?.companyId, shifts.length]); // Added shifts.length to prevent unnecessary refetches

  // Check if employee has shift assignment
  useEffect(() => {
    if (selectedEmployee && fromDate) {
      // This will be handled by EmployeeShiftInfo component
      // We just need to know if we should show shift selection
      // Only reset shift selection if employee changes, not date changes
      if (!showShiftSelection) {
        setShiftDataLoaded(false);
        setHasWorkingHours(true);
        setTimeValidationError(null);
      }
    }
  }, [selectedEmployee, fromDate, showShiftSelection]);

  // Handle shift data loaded callback - simplified for better performance
  const handleShiftDataLoaded = useCallback((hasWorkingHoursForDate: boolean) => {
    setShiftDataLoaded(true);
    setHasWorkingHours(hasWorkingHoursForDate);
    // Remove timeValidationError reset to prevent unnecessary re-renders
  }, []);

  // Handle date changes for shift validation (without resetting selection)
  useEffect(() => {
    if (fromDate && selectedShiftData) {
      // Re-validate the selected shift for the new date
      const hasWorkingHoursForNewDate = hasShiftWorkingHoursForDate(selectedShiftData.day_time_deduction, fromDate);
      setHasWorkingHours(hasWorkingHoursForNewDate);
      setTimeValidationError(null);
    }
  }, [fromDate, selectedShiftData]);

  // Update working hours status when shift is selected from dropdown
  useEffect(() => {
    if (selectedShiftData && fromDate) {
      const hasWorkingHoursForSelectedShift = hasShiftWorkingHoursForDate(selectedShiftData.day_time_deduction, fromDate);
      setHasWorkingHours(hasWorkingHoursForSelectedShift);
      setTimeValidationError(null);
    }
  }, [selectedShiftData, fromDate]);

  // Validate overtime time against shift working hours based on in_working_hours field
  const validateOvertimeTime = useCallback((startTime: string, endTime: string) => {
    if (!startTime || !endTime) {
      return { isValid: true, error: null };
    }

    // For assigned shifts, use the hasWorkingHours state
    if (shiftDataLoaded && !showShiftSelection) {
      if (!hasWorkingHours) {
        return { isValid: true, error: null }; // No validation needed if no working hours
      }
      // For assigned shifts with working hours, we need to get the shift data
      // This will be handled by the shift overlap validation in the form hook
      return { isValid: true, error: null };
    }

    // For dropdown-selected shifts, check the selected shift data
    if (showShiftSelection && selectedShiftData) {
      // Skip validation on holidays - all hours are overtime
      if (isHoliday) {
        return { isValid: true, error: null };
      }
      
      if (!hasShiftWorkingHoursForDate(selectedShiftData.day_time_deduction, fromDate)) {
        return { isValid: true, error: null }; // No validation needed if no working hours
      }

      // Get shift times for validation
      const otDateObj = new Date(fromDate);
      const dayNumber = getShiftDayNumber(otDateObj);
      
      const shiftForDate = selectedShiftData.day_time_deduction.find((dayGroup) => {
        if (!dayGroup || typeof dayGroup !== 'object') return false;
        if (Array.isArray(dayGroup.day)) {
          return dayGroup.day.includes(dayNumber as 1 | 2 | 3 | 4 | 5 | 6 | 7);
        }
        return dayGroup.day === dayNumber;
      });

      if (!shiftForDate) {
        return { isValid: true, error: null };
      }

      const shiftStart = shiftForDate.start_time;
      const shiftEnd = shiftForDate.end_time;

      // Use the proper utility function for overlap detection (handles cross-midnight)
      const overlaps = isOvertimeOverlapWithShift(startTime, endTime, shiftStart, shiftEnd);
      
      // Apply different validation based on in_working_hours field
      if (selectedShiftData.in_working_hours) {
        // If in_working_hours = TRUE: OT must be COMPLETELY WITHIN shift hours
        const isCompletelyWithin = isOvertimeCompletelyWithinShift(startTime, endTime, shiftStart, shiftEnd);
        if (!isCompletelyWithin) {
          return { 
            isValid: false, 
            error: `For this shift, overtime must be completely within shift hours (${shiftStart} - ${shiftEnd})` 
          };
        }
      } else {
        // If in_working_hours = FALSE: OT must NOT overlap with shift hours (existing logic)
        if (overlaps) {
          return { 
            isValid: false, 
            error: `Overtime cannot overlap with shift hours (${shiftStart} - ${shiftEnd})` 
          };
        }
      }
    }

    return { isValid: true, error: null };
  }, [selectedShiftData, hasWorkingHours, fromDate, shiftDataLoaded, showShiftSelection, isHoliday]);

  // Update validation error when needed (separate from validation logic)
  const updateTimeValidationError = useCallback(() => {
    if (startTime && endTime) {
      const validation = validateOvertimeTime(startTime, endTime);
      setTimeValidationError(validation.error);
    } else {
      setTimeValidationError(null);
    }
  }, [startTime, endTime, validateOvertimeTime]);

  // Clear validation error when times change or shift selection changes
  useEffect(() => {
    if (startTime && endTime) {
      // Update validation error when times change or shift is selected
      updateTimeValidationError();
    } else {
      setTimeValidationError(null);
    }
  }, [startTime, endTime, selectedShiftData, updateTimeValidationError]);

  // Handle shift selection - simplified without category logic
  const handleShiftChange = (shiftId: string) => {
    setSelectedShiftId(shiftId);
    
    // Find the selected shift from filtered shifts (which includes duty roster constraints)
    const shift = filteredShifts.find((s: Shift) => s.id === shiftId);
    
    if (shift) {
      // Always set the selected shift data so the info table shows
      // The working hours validation will be handled by the ShiftInfoTable component
      setSelectedShiftData(shift);
      
      // Don't auto-populate timing - let user select manually
      // Note: We don't validate working hours here anymore - let the table handle it
    }
  };

  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="w-full max-w-[90vw] sm:max-w-[480px] md:max-w-[500px] p-3 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="text-lg">New Overtime Request</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 py-2">
          {/* Row 0: Alerts */}
          {isHoliday && holidayInfo && (
            <div className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-md border border-amber-200 dark:border-amber-700">
              🎉 {holidayInfo.name} - All hours are overtime
            </div>
          )}
          {!isHoliday && !hasShiftOverlap && willDeductBreak && breakDeductionNotice && (
            <div className="text-xs text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/20 px-2 py-1.5 rounded border border-blue-200 dark:border-blue-700">
              {breakDeductionNotice}
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-3 items-end">
              {/* Employee Selection */}
              <div className="col-span-8 space-y-1">
                <Label className="text-xs font-medium flex items-center gap-1.5">
                  Employee
                  {selectedEmployee && (
                    <span className="text-muted-foreground font-normal text-xs">(This month: {monthlyHours}h)</span>
                  )}
                </Label>
                <SearchEmployeeInput 
                  value={selectedEmployee?.id || ''}
                  onChange={setSelectedEmployee}
                  userRole={userRole}
                  userDepartment={userDepartment}
                  hideLabel
                />
              </div>
              
              {/* Date Picker */}
              <div className="col-span-4 space-y-1">
                <Label className="text-xs font-medium">Date</Label>
                <Input 
                  type="date" 
                  value={fromDate} 
                  onChange={(e) => setFromDate(e.target.value)}
                  className="h-8 date-input text-xs"
                  min={getMinOvertimeDate()}
                />
              </div>
            </div>
          </div>

            {/* Shift Selection Row - Only show when no shift is assigned */}
            {selectedEmployee && fromDate && showShiftSelection && (
              <div className="space-y-2">
                {/* Use the unified ShiftSelector component */}
                <ShiftSelector
                  shifts={filteredShifts}
                  selectedShiftId={selectedShiftId}
                  onShiftChange={handleShiftChange}
                  label="Select Shift for Overtime"
                  placeholder="Select a shift"
                  compact={true}
                />
                
                {/* Selected Shift Information Table - Use the new ShiftInfoTable component */}
                {selectedShiftData && (
                  <ShiftInfoTable
                    shiftData={selectedShiftData}
                    otDate={fromDate}
                    showNoWorkingHoursMessage={!hasShiftWorkingHoursForDate(selectedShiftData.day_time_deduction, fromDate)}
                  />
                )}
              </div>
            )}
              
          {/* Shift Information */}
          {selectedEmployee && fromDate && (
            <div className="space-y-2">
              <EmployeeShiftInfo 
                employeeId={selectedEmployee.id}
                employeeName={`${selectedEmployee.firstName || ''} ${selectedEmployee.lastName || ''}`.trim() || selectedEmployee.name || 'Unknown Employee'}
                otDate={fromDate}
                className="text-xs"
                  onShiftSelectionNeeded={(needed) => setShowShiftSelection(needed)}
                  onShiftDataLoaded={handleShiftDataLoaded}
              />
            </div>
          )}
              
          {/* Time Details */}
          <div className="space-y-2">
            
            <div className="grid grid-cols-12 gap-3 items-end">
              {/* Left group: Start + End (left aligned) */}
              <div className="col-span-8">
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-6 space-y-1">
                    <Label className="text-xs font-medium">Start Time</Label>
                    <Input 
                      type="time" 
                      value={startTime} 
                      onChange={e => setStartTime(e.target.value)} 
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="col-span-6 space-y-1">
                    <Label className="text-xs font-medium">End Time</Label>
                    <Input 
                      type="time" 
                      value={endTime} 
                      onChange={e => setEndTime(e.target.value)} 
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Right group: Total Hours (right aligned) */}
              <div className="col-span-4">
                <div className="flex justify-end">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Total Hours</Label>
                    <Input 
                      value={totalHours !== null ? `${totalHours.toFixed(2)}h` : '—'}
                      className="h-8 w-24 bg-muted/30 border-border text-foreground font-medium text-xs"
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Justification */}
          <div className="space-y-1">
            <JustificationInput
              justification={justification}
              onJustificationChange={setJustification}
            />
          </div>

        </div>

        <DialogFooter className="pt-3 px-3">
          <div className="flex w-full items-center justify-between gap-3">
            {/* Compact inline messages on the left */}
            <div className="flex flex-col gap-1 max-w-[60%]">
              {shiftOverlapError && !isHoliday && (
                <div className="text-xs text-destructive/90 bg-destructive/10 px-2 py-1 rounded border border-destructive/20 leading-snug">
                  Overlaps shift hours. Pick time outside shift.
                </div>
              )}
              {totalHoursError && (
                <div className="text-xs text-destructive/90 bg-destructive/10 px-2 py-1 rounded border border-destructive/20 leading-snug">
                  Invalid duration. Check start/end.
                </div>
              )}
                {timeValidationError && (
                  <div className="text-xs text-destructive/90 bg-destructive/10 px-2 py-1 rounded border border-destructive/20 leading-snug">
                    {timeValidationError}
                </div>
              )}
              {error && (
                <div className="text-xs text-destructive/90 bg-destructive/10 px-2 py-1 rounded border border-destructive/20 leading-snug">
                  {error}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} className="h-8 text-xs px-3">
                Cancel
              </Button>
              <Button
                  onClick={(e) => {
                    // INSTANT modal close for better UX - close immediately on click
                    onOpenChange(false);
                    
                    // Start submission in background
                    handleSubmit(e, { shiftDataLoaded: true });
                  }} 
                  disabled={isSubmitDisabled} 
                className="h-8 text-xs px-3"
              >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                      Submitting...
                    </>
                  ) : (
                    "Submit Request"
                  )}
              </Button>
            </div>
          </div>
        </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
